import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChatbotliveService } from 'src/app/core/services/chatbot_live.service';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Subject, takeUntil } from 'rxjs';
import { GetBotResponse } from 'src/app/models/bot.interface';

dayjs.locale('es');
@Component({
  selector: 'globot-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss'],
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  private destroy$ = new Subject<void>();
  errorMessages: { [key: string]: string } = {};
  originalValues: { [key: string]: string } = {};

  loading: boolean = false;
  success: boolean = false;

  created_at: string = '';
  channel: string = '';
  name: string | null = null;
  phone: string | null = null;
  email: string | null = null;
  sender_requirement: string | null = null;
  correlation_id: string = '';
  formChanged: boolean = false;
  bot?: GetBotResponse;

  constructor(private chatbotliveService: ChatbotliveService, private route: ActivatedRoute) {}

  ngOnInit() {
    if (localStorage.getItem('bot')) {
      this.bot = JSON.parse(localStorage.getItem('bot')!);
    }
    this.chatbotliveService.customerData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        this.resetForm();
        this.created_at = data.created_at;
        this.channel = data.channel;
        this.name = data.name;
        this.phone = data.phone;
        this.email = data.email;
        this.sender_requirement = data.sender_requirement;
        this.correlation_id = data.correlation_id;

        // Guardar los valores originales
        this.originalValues = {
          sender_name: this.name ?? '',
          sender_email: this.email ?? '',
          sender_phone: this.phone ?? '',
          sender_requirement: this.sender_requirement ?? '',
        };
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetForm() {
    this.loading = false;
    this.success = false;
    this.formChanged = false;
    this.errorMessages = {};
  }

  handleInput(field: string, event: Event) {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    const originalField = this.getOriginalFieldName(field);
    (this as any)[originalField] = value;

    this.validateField(field, value);

    // Limpiar mensajes de error específicos para email/teléfono si al menos uno tiene contenido
    if (field === 'sender_email' || field === 'sender_phone') {
      const hasEmail = this.email && this.email.trim() !== '';
      const hasPhone = this.phone && this.phone.trim() !== '';

      if (hasEmail || hasPhone) {
        this.errorMessages['sender_email'] = '';
        this.errorMessages['sender_phone'] = '';
      }
    }

    this.checkFormChanged();
  }

  checkFormChanged() {
    this.formChanged =
      this.name !== this.originalValues['sender_name'] ||
      this.email !== this.originalValues['sender_email'] ||
      this.phone !== this.originalValues['sender_phone'] ||
      this.sender_requirement !== this.originalValues['sender_requirement'];
  }

  cancelChanges() {
    this.name = this.originalValues['sender_name'];
    this.email = this.originalValues['sender_email'];
    this.phone = this.originalValues['sender_phone'];
    this.sender_requirement = this.originalValues['sender_requirement'];
    this.formChanged = false;
    this.errorMessages = {};
    this.cancelled.emit();
  }

  saveChanges() {
    if (!this.validateNameAndContacts()) return;

    this.loading = true;
    const changedFields = this.getChangedFields();

    this.chatbotliveService
      .updateCustomerField(this.bot?.id!, this.correlation_id, changedFields)
      .subscribe({
        next: (response) => this.handleUpdateSuccess(response),
        error: (error) => this.handleUpdateError(error),
      });
  }

  private validateNameAndContacts(): boolean {
    const isValidName = this.validateField('sender_name', this.name ?? '');
    if (!isValidName) return false;

    const hasValidContact = this.validateContactPresence() && this.validateEmailAndPhone();
    return hasValidContact;
  }

  private validateContactPresence(): boolean {
    const hasContact = !!this.email?.trim() || !!this.phone?.trim();

    if (!hasContact) {
      this.setContactErrors();
      return false;
    }
    return true;
  }

  private setContactErrors() {
    this.errorMessages['sender_email'] = 'Es necesario al menos un email';
    this.errorMessages['sender_phone'] = 'Es necesario al menos un teléfono';
  }

  private validateEmailAndPhone(): boolean {
    let isValid = true;

    if (this.email?.trim()) {
      isValid = this.validateField('sender_email', this.email ?? '') && isValid;
    }

    if (this.phone?.trim()) {
      isValid = this.validateField('sender_phone', this.phone ?? '') && isValid;
    }

    return isValid;
  }

  private getChangedFields(): any {
    const changedFields: any = {};

    this.updateChangedField(changedFields, 'sender_name', 'name');
    this.updateChangedField(changedFields, 'sender_email', 'email', true);
    this.updateChangedField(changedFields, 'sender_phone', 'phone', true);
    this.updateChangedField(changedFields, 'sender_requirement', 'sender_requirement', true);

    return changedFields;
  }

  private updateChangedField(
    fields: any,
    originalKey: string,
    fieldKey: keyof CustomerFormComponent,
    allowNull = false
  ) {
    const currentValue = this[fieldKey] as string | null;
    const originalValue = this.originalValues[originalKey];

    if (currentValue !== originalValue) {
      if (allowNull) {
        fields[originalKey] = this.determineNullifiedValue(currentValue);
      } else {
        fields[originalKey] = currentValue;
      }
    }
  }

  private determineNullifiedValue(value: string | null): string | null {
    return value?.trim() ? value : null;
  }

  private handleUpdateSuccess(_response: any) {
    this.loading = false;
    this.success = true;
    this.updateOriginalValues();
    this.formChanged = false;
    this.chatbotliveService.updateCustomerData({
      created_at: this.created_at,
      channel: this.channel,
      name: this.name,
      phone: this.phone,
      email: this.email,
      sender_requirement: this.sender_requirement,
      correlation_id: this.correlation_id,
    });
    setTimeout(() => {
      this.success = false;
      this.saved.emit();
    }, 1500);
  }

  private updateOriginalValues() {
    this.originalValues = {
      sender_name: this.name ?? '',
      sender_email: this.email?.trim() ? this.email : '',
      sender_phone: this.phone?.trim() ? this.phone : '',
      sender_requirement: this.sender_requirement?.trim() ? this.sender_requirement : '',
    };
  }

  private handleUpdateError(error: any) {
    console.error('Error al actualizar los campos:', error);
    this.loading = false;
    this.cancelChanges();
  }

  private getOriginalFieldName(field: string): string {
    const fieldMap: { [key: string]: string } = {
      sender_name: 'name',
      sender_email: 'email',
      sender_phone: 'phone',
      sender_requirement: 'sender_requirement',
    };
    return fieldMap[field] || field;
  }

  getFormattedDate(date: string): string {
    return date ? dayjs(date).format('DD [de] MMM YYYY / hh:mm a') : '';
  }

  validateField(field: string, value: string): boolean {
    // Si es el campo requirement, siempre es válido (opcional)
    if (field === 'sender_requirement') {
      this.errorMessages[field] = '';
      return true;
    }

    // Para campos diferentes a requirement, validar que no estén vacíos
    if (field !== 'sender_requirement' && value.trim() === '') {
      this.errorMessages[field] = 'Campo vacío';
      return false;
    }

    if (value.trim().length < 2 && field === 'sender_name') {
      this.errorMessages[field] = 'Debe tener al menos 2 caracteres';
      return false;
    }

    switch (field) {
      case 'sender_email': {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          this.errorMessages[field] = 'Email inválido';
          return false;
        }
        break;
      }
      case 'sender_phone': {
        const phoneRegex = /^\+?[\d\s-]{8,}$/;
        if (!phoneRegex.test(value)) {
          this.errorMessages[field] = 'Número de teléfono inválido';
          return false;
        }
        break;
      }
    }

    this.errorMessages[field] = '';
    return true;
  }

  validateNumberInput(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];

    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
}
